import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Accordion,
    AccordionItem,
    ButtonPrimary,
    Circle,
    DataCard,
    Divider,
    IconChatRegular,
    skinVars,
    Text1,
    Text2,
    Text6,
    Title3,
} from '@telefonica/mistica';
import './index.scss';
import { getClaimDetail } from '../../../../../services/BillService';
import HeaderComponent from '../../../../../components/HeaderComponent';
import { isLandlinePhoneNumber, setTypeTagstatus } from '../../../utils/functions';
import { formatPrices } from '../../../../../utils/dataTransformation/unitsConversion';
import { getFormatDate } from '../../../../../utils/dataTransformation/dateConversions';
import { IClaimDetail } from '../../../../../models/Bills.interfaces';
import { t } from 'i18next';
import { useBillsContext } from '../../../../../context/bills-context/BillsContext';

const ClaimDetail: React.FC = () => {
    const navigate = useNavigate();
    const { claimId } = useBillsContext();
    const [claimDetail, setClaimDetail] = useState<IClaimDetail>({
        id: '',
        code: '',
        totalDisputedAmount: '',
        customerComments: '',
        phase: '',
        // @ts-ignore
        claimedCustomerAccounts: [],
        type: '',
        emisionBillDate: '',
        creationDate: '',
        endDate: '',
    });
    const navigationBackHandler = () => navigate('/claims/claim-list');

    useEffect(() => {
        claimDetailRequest();
    }, []);

    const claimDetailRequest = async () => {
        if (!claimId) return;

        try {
            const response = await getClaimDetail(claimId);
            setClaimDetail(response.data);
        } catch (error) {
            console.error(error);
        }
    };

    const getClaimDetailTitleKey = () => {
        const accounts = claimDetail.claimedCustomerAccounts;
        if (
            accounts &&
            accounts.length > 0 &&
            accounts[0]?.claimedCustomerBills &&
            accounts[0].claimedCustomerBills.length > 0
        ) {
            const disputedAmountCreateds = accounts[0].claimedCustomerBills[0].disputedAmountCreateds;
            if (disputedAmountCreateds && disputedAmountCreateds.length > 0) {
                if (disputedAmountCreateds[0].appliedCustomerBillingRates[0].typeDescription)
                    return 'billsClaimDetailConsumptionTitle';
            }
        }
        return 'billsClaimDetailQuotaTitle';
    };

    const getClaimDetailName = (claim: any) => {
        if (claim.text) return claim.text;

        switch (claim.typeDescription) {
            case 'Llamada':
                return `${claim.typeDescription} ${
                    isLandlinePhoneNumber(claim.product?.identifier || '') ? t('mobile') : t('permanent')
                } - ${claim.product?.identifier || ''}`;
            case 'Internet':
                return `${claim.typeDescription} - ${claim.product?.identifier || ''}`;
            case 'Mensaje':
                return `${claim.typeDescription} - ${claim.product?.identifier || ''}`;
            case 'Contenidos':
                return t('extraconsumption');
        }
    };

    return (
        <div className='.bills-claimDetail-page'>
            <HeaderComponent
                title={t('ClaimDetails')}
                navigationBarOnBack={navigationBackHandler}
                breadcrumbs={[
                    {
                        url: '/bills',
                        title: t('invoice'),
                    },
                    {
                        url: '/claims/claim-list',
                        title: t('billsClaimsHistory'),
                    },
                ]}
            />
            <div className='bills-claimDetail-page__container'>
                <div>
                    <div className='bills-claimDetail-page__list'>
                        <div
                            key={claimDetail.id}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '16px',
                            }}
                        >
                            <div>{setTypeTagstatus(claimDetail.phase)}</div>
                            <div>
                                <Text6 color={skinVars.colors.textPrimary}>{t(getClaimDetailTitleKey())}</Text6>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <Text2 regular color={skinVars.colors.textSecondary}>
                                    {t('billClaimDetailCode')}
                                    {claimDetail.code}
                                </Text2>
                                <Text2 regular color={skinVars.colors.textSecondary}>
                                    {t('billsClaimCreationDate')}{' '}
                                    {getFormatDate(claimDetail.emisionBillDate, 'DD/MM/YYYY')}
                                </Text2>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <Text2 regular color={skinVars.colors.textPrimary}>
                                    {t('billsClaimComment')}
                                </Text2>
                                <Text2 regular color={skinVars.colors.textSecondary}>
                                    {claimDetail.customerComments}
                                </Text2>
                            </div>
                            {claimDetail.totalDisputedAmount && (
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <Text2 regular color={skinVars.colors.textPrimary}>
                                        {t('billsClaimResolve')}
                                    </Text2>
                                    <Text2 regular color={skinVars.colors.textSecondary}>
                                        {formatPrices(Math.abs(+claimDetail.totalDisputedAmount), 'spent')}
                                    </Text2>
                                </div>
                            )}
                            <Divider />
                        </div>
                    </div>
                    <div>
                        <Accordion singleOpen={true}>
                            {claimDetail.claimedCustomerAccounts.length > 0 &&
                                claimDetail.claimedCustomerAccounts[0]?.claimedCustomerBills[0].disputedAmountCreateds.map(
                                    (claim: any) => (
                                        <AccordionItem
                                            key={claim.id}
                                            title=''
                                            asset={
                                                <div className='bills-claimDetail-page__accordion'>
                                                    <Title3>
                                                        {getClaimDetailName(claim.appliedCustomerBillingRates[0])}
                                                    </Title3>
                                                    <div>
                                                        <Text1 regular color={skinVars.colors.textPrimary}>
                                                            {t('claimCost') + ': '}
                                                        </Text1>
                                                        <Text2 regular color={skinVars.colors.textSecondary}>
                                                            {formatPrices(Math.abs(claim.disputedAmount), 'spent')}
                                                        </Text2>
                                                    </div>
                                                    <div>
                                                        <div>
                                                            <Text1 regular color={skinVars.colors.textPrimary}>
                                                                {t('opening')}
                                                            </Text1>
                                                            <Text2 regular color={skinVars.colors.textSecondary}>
                                                                {getFormatDate(claimDetail.creationDate, 'DD/MM/YYYY')}
                                                            </Text2>
                                                        </div>
                                                        <div>
                                                            <Text1 regular color={skinVars.colors.textPrimary}>
                                                                {t('closing')}
                                                            </Text1>
                                                            <Text2 regular color={skinVars.colors.textSecondary}>
                                                                {claimDetail.endDate
                                                                    ? getFormatDate(claimDetail.endDate, 'DD/MM/YYYY')
                                                                    : '--'}
                                                            </Text2>
                                                        </div>
                                                    </div>
                                                </div>
                                            }
                                            content={
                                                <div className='bills-claimDetail-page__accordion-content'>
                                                    <div>
                                                        <Text1 regular color={skinVars.colors.textPrimary}>
                                                            {t('concept')}
                                                        </Text1>
                                                        <Text2 regular color={skinVars.colors.textSecondary}>
                                                            {claim.appliedCustomerBillingRates[0].typeDescription
                                                                ? t('extraConsumptions')
                                                                : t('monthlyQuota')}
                                                        </Text2>
                                                    </div>
                                                    <div>
                                                        <Text1 regular color={skinVars.colors.textPrimary}>
                                                            {t('claimSummaryReason')}
                                                        </Text1>
                                                        <Text2 regular color={skinVars.colors.textSecondary}>
                                                            {claim.motive}
                                                        </Text2>
                                                    </div>
                                                    <div>
                                                        <Text1 regular color={skinVars.colors.textPrimary}>
                                                            {t('claimSummarySecondaryReason')}
                                                        </Text1>
                                                        <Text2 regular color={skinVars.colors.textSecondary}>
                                                            {claim.subMotive}
                                                        </Text2>
                                                    </div>
                                                    {claim.resolutionType && (
                                                        <div>
                                                            <Text1 regular color={skinVars.colors.textPrimary}>
                                                                {t('resolution')}
                                                            </Text1>
                                                            <Text2 regular color={skinVars.colors.textSecondary}>
                                                                {claim.resolutionType}
                                                            </Text2>
                                                        </div>
                                                    )}
                                                </div>
                                            }
                                        />
                                    ),
                                )}
                        </Accordion>
                    </div>
                </div>
                <div className='bills-claimDetail-page__communications'>
                    <DataCard
                        title={t('Communications')}
                        description={t('communicationsDescription')}
                        slot={
                            <ButtonPrimary
                                small
                                onPress={() => navigate(`/claims/communications-list?code=${claimDetail.code}`)}
                            >
                                {t('contentLoginButton')}
                            </ButtonPrimary>
                        }
                        asset={
                            <Circle size={40} backgroundColor={skinVars.colors.brandLow}>
                                <IconChatRegular size={24} color={skinVars.colors.brand} />
                            </Circle>
                        }
                    />
                </div>
            </div>
        </div>
    );
};

export default ClaimDetail;
